import {
  Address,
  ExperienceKeys,
  MerchantConfig,
  MerchantPaymentProduct,
  MerchantProgramValues,
  MerchantDetails,
  MoneyValue,
  Name,
  Order,
  PaymentType,
  Program,
  FilteredApplication,
  FilteredCustomerCloseData,
  DynamicDisclosure,
} from '@ads-bread/shared/bread/codecs';

export type CheckoutPublicXPropsOptions = {
  /** The name of the buyer. */
  name?: Name;
  /** The billing address of the buyer. */
  billingAddress?: Address;
  /** The shipping address of the buyer. */
  shippingAddress?: Address;
  /** The email of the buyer. */
  email?: string;
  /** The phone of the buyer. */
  phone?: string;
  /** The callback to open modal. */
  triggerOpenCardModal?: (placementInfo: {
    placementId?: string;
    location?: string;
  }) => void;
  /** The buyerID. */
  buyerID?: string;
  /** The payment agreement ID. */
  paymentAgreementID?: string;
  /** The callback for add to cart. */
  onAddToCart?: (order?: Order) => void;
  /** The callback for customer open. */
  onCustomerOpen?: (locationType: LocationType, placement: unknown) => void;
  /** The callback for customer close. */
  onCustomerClose?: (data: FilteredCustomerCloseData) => void;
  /** The callback for approved applications. */
  onApproved: (data: FilteredApplication) => void;
  /** The callback when checking out. */
  onCheckout: (data: FilteredApplication) => void;
  onOneTrustConsent?: (data: OneTrustConsentData) => void;
};

export type LocationType =
  | 'Homepage'
  | 'Landing'
  | 'Search'
  | 'Product'
  | 'Category'
  | 'Banner'
  | 'Checkout'
  | 'Cart'
  | 'Mobile'
  | 'Loyalty'
  | 'Footer'
  | 'Bag'
  | 'Dashboard'
  | 'PDP'
  | 'Financing';

export type OneTrustConsentData = {
  detail: string[];
};

export type PlacementOptions = {
  /** The allow checkout flag. */
  allowCheckout: boolean;
  /** The dom ID for the placement. */
  domID: string;
  /** The location type. */
  locationType: LocationType;
  preview?: boolean;
  /** The order. */
  order?: Order;
};

export type CheckoutSDKOnlyOptions = {
  integrationKey: string;
  containerID?: string;
  apiURL?: string;
  onInitialized?: () => void;
  onInitializeFailure?: () => void;
  onLog?: (
    level: 'WARN' | 'ERROR',
    message: string,
    context: Record<string, unknown>
  ) => void;
};

export const DURATION_DESIGNATORS = ['M', 'W'] as const;
export type DurationDesignator = typeof DURATION_DESIGNATORS[number];

export type Duration = {
  /** The length. */
  length: number;
  /** The designator of monthly and Weekly. */
  designator: DurationDesignator;
};

// Values computed from the MerchantPaymentProducts of a given
// type, i.e. either SPLITPAY or INSTALLMENTS
export type ComputedPaymentTypeValues = {
  exampleTotalPrice: MoneyValue;
  lengthOptions: number[];
  promoOfferRate: number;
  promoOfferFrequency: Duration;
  minCartSize: number;
  maxCartSize: number;
  promoOfferLength: number;
  maxInterestRateBPS: number;
  promoOfferAmount: MoneyValue;
  minInterestRateBPS: number;
};

export type PromoOffer = {
  /** The rate on the offer. */
  rate: number;
  /** The payment amount. */
  paymentAmount: MoneyValue; // TODO: Make this type 'Money'
  /** The frequency of payment. */
  frequency: Duration;
  /** The payment type of the offer. */
  type: PaymentType;
  /** The number of payments. */
  numberOfPayments: number;
};

export type AnalyticValues = {
  /** The entry point in which the modal is opened. */
  experienceEntrypoint: string;
  /**  The location in which the modal is opened. */
  locationType: LocationType;
};

export type CheckoutPrivateOptions = {
  /** Correlation ID supplied by the SDK for a hosted checkout journey. */
  correlationID?: string;
  /** A boolean to determine display mode. */
  modal: boolean;
  /** The merchant config. */
  merchantConfig?: MerchantConfig;
  /** The merchant payment products. */
  merchantPaymentProducts?: MerchantPaymentProduct[];
  /** The merchant program values. */
  merchantProgramValues?: MerchantProgramValues;
  /** The program. */
  program?: Program;
  /** The promo offers. */
  promoOffers?: PromoOffer[];
  /** The app ID. */
  appID: string;
  /** The merchant ID. */
  merchantID: string;
  /** The merchant's domain name */
  merchantOrigin: string;
  /** The program ID. */
  programID: string;
  /** The location ID. */
  locationID: string;
  /** The display interest rate. */
  displayInterestRateBPS?: number;
  /** The merchant details. */
  merchantDetails?: MerchantDetails;
  /** The dynamic disclosure. */
  dynamicDisclosure?: DynamicDisclosure;
};

// Make `order` required for checkout app but not SDK
export type CheckoutXPropOptions = CheckoutPublicXPropsOptions & {
  /** The order. */
  order: Order;
  /** A boolean to determine default order. */
  isDefaultOrder: boolean;
  /** A boolean to determine if in embedded mode. */
  isEmbedded: boolean;
  /** A boolean to determine if in preview mode. */
  isPreview: boolean;
  /** A boolean to determine if checkout is enabled */
  allowCheckout: boolean;
  /** The modID to attach to. */
  domID: string;
  /** The analytics values. */
  analytics: AnalyticValues;
  /** Generated ID for feature flag service */
  deviceID: string;
  /** Boolean to determine if a location is disabled */
  isLocationEnabled: boolean;
  /** The location type. */
  locationType: PlacementOptions['locationType'];
} & CheckoutPrivateOptions;

export type CheckoutSDKPublicOptions = CheckoutPublicXPropsOptions &
  CheckoutSDKOnlyOptions;

export type BreadExperienceConfig = Pick<
  ExperienceKeys,
  'appID' | 'merchantID' | 'programID' | 'locationID'
>;

export type MerchantUICustomization = Pick<
  MerchantConfig,
  | 'checkoutPrimaryColor'
  | 'checkoutSecondaryColor'
  | 'logoUrl'
  | 'disclosureSymbolInstallments'
  | 'disclosureSymbolSplitpay'
  | 'disclosureSymbolSplitpayInterest'
>;
